/**
 * Command Executor Hook
 * 
 * Komutları çalıştırır (navigation, action, dynamic)
 * Zero prop drilling pattern
 */

import { useNavigate } from 'react-router-dom';
import { useSlashCommandStore } from '../store/slash-command-store';
import { useChatStore } from '@/features/chat';
import type { SlashCommand, CommandExecutionResult } from '../types/command-types';

export const useCommandExecutor = (customSetMessage?: (message: string) => void) => {
  const navigate = useNavigate();
  const { addToHistory, closeMenu, setActiveCommandPath } = useSlashCommandStore();
  const { setMessage: defaultSetMessage } = useChatStore();
  
  // Custom setMessage varsa onu kullan, yoksa default'u kullan
  const setMessage = customSetMessage || defaultSetMessage;
  
  /**
   * Execute Command
   * 
   * Command type'a göre uygun action'ı çalıştırır
   */
  const executeCommand = async (command: SlashCommand): Promise<CommandExecutionResult> => {
    try {
      // History'ye ekle (navigation ve action için)
      if (command.type === 'navigation' || command.type === 'action') {
        addToHistory({
          id: command.id,
          trigger: command.trigger,
          label: command.label,
        });
      }
      
      // Command type'a göre işlem yap
      switch (command.type) {
        case 'navigation':
          if (command.path) {
            // Navigate
            navigate(command.path);
            // Menu'yü kapat
            closeMenu();
            // Input'u temizle
            setMessage('');
            return { success: true, message: 'Navigated' };
          }
          return { success: false, message: 'No path specified' };
          
        case 'action':
          if (command.action) {
            // Action çalıştır
            await command.action();
            // Menu'yü kapat
            closeMenu();
            // Input'u temizle
            setMessage('');
            return { success: true, message: 'Action executed' };
          }
          return { success: false, message: 'No action specified' };
          
        case 'parent':
          // Parent command - children'ları göster
          if (command.children) {
            // Active path'i güncelle
            setActiveCommandPath([command.trigger]);
            // Input'u parent command trigger'ına güncelle (children'ları göstermek için)
            setMessage(command.trigger);
            return { success: true, message: 'Expanded children' };
          }
          return { success: false, message: 'No children' };
          
        case 'dynamic':
          // Dynamic command - loader çalışacak
          // Active path'i güncelle
          setActiveCommandPath([command.trigger]);
          // Input'u dynamic command trigger'ına güncelle
          setMessage(command.trigger);
          return { success: true, message: 'Dynamic loader will run' };
          
        default:
          return { success: false, message: 'Unknown command type' };
      }
    } catch (error) {
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  };
  
  /**
   * Execute Command by ID
   * 
   * ID'ye göre command bulur ve çalıştırır
   */
  const executeCommandById = async (id: string): Promise<CommandExecutionResult> => {
    const { findCommandById } = await import('../registry/command-registry');
    const command = findCommandById(id);
    
    if (!command) {
      return { success: false, message: 'Command not found' };
    }
    
    return executeCommand(command);
  };
  
  /**
   * Execute Command by Trigger
   * 
   * Trigger string'ine göre command bulur ve çalıştırır
   */
  const executeCommandByTrigger = async (trigger: string): Promise<CommandExecutionResult> => {
    const { findCommandByTrigger } = await import('../registry/command-registry');
    const command = findCommandByTrigger(trigger);
    
    if (!command) {
      return { success: false, message: 'Command not found' };
    }
    
    return executeCommand(command);
  };
  
  return {
    executeCommand,
    executeCommandById,
    executeCommandByTrigger,
  };
};

